import focusBearApi from 'services/axios-config';
import {
  MANAGED_API_PREFIX,
  MANAGED_HISTORY_LIMIT
} from 'constants/managed-blocking';
import {
  ManagedContext,
  ManagedCategories,
  ManagedPolicy,
  ManagedPolicyInput,
  ManagedStudent,
  ManagedYearTagArgs,
  ManagedYearTagResult,
  ManagedParentGrant,
  ManagedPolicyHistory,
  ManagedSettingsRequest,
  ManagedViolation,
  ManagedAppeal,
  ManagedViolationFilter,
  ManagedSaveArgs,
  ManagedAssignmentArgs,
  ManagedInviteArgs,
  ManagedCommitArgs,
  ManagedEffectivePolicies,
  ManagedUrlPreview,
  ManagedUrlTestArgs,
  ManagedAppTestArgs,
  ManagedAppPreview
} from 'interfaces/managed-blocking.interface';

const get = async <T>(path: string) =>
  (await focusBearApi.get<T>(`${MANAGED_API_PREFIX}${path}`)).data;
const post = async <T>(path: string, body: unknown = {}) =>
  (await focusBearApi.post<T>(`${MANAGED_API_PREFIX}${path}`, body)).data;
const remove = async (path: string) => {
  await focusBearApi.delete(`${MANAGED_API_PREFIX}${path}`);
};
export const getManagedContext = () => get<ManagedContext>('/context');
export const getManagedCategories = () => get<ManagedCategories>('/categories');
export const getSchoolPolicies = (schoolId: string) =>
  get<ManagedPolicy[]>(`/schools/${encodeURIComponent(schoolId)}/policies`);
export const createSchoolPolicy = (
  schoolId: string,
  input: ManagedPolicyInput
) =>
  post<ManagedPolicy>(
    `/schools/${encodeURIComponent(schoolId)}/policies`,
    input
  );
export const saveManagedPolicy = async ({ policy, changes }: ManagedSaveArgs) =>
  (
    await focusBearApi.put<ManagedPolicy>(
      `${MANAGED_API_PREFIX}/policies/${encodeURIComponent(policy.id)}`,
      { ...changes, expected_revision: policy.revision }
    )
  ).data;
export const publishManagedPolicy = (policy: ManagedPolicy) =>
  post<ManagedPolicy>(`/policies/${encodeURIComponent(policy.id)}/publish`, {
    expected_revision: policy.revision
  });
export const getSchoolStudents = (schoolId: string) =>
  get<ManagedStudent[]>(`/schools/${encodeURIComponent(schoolId)}/students`);
export const tagManagedStudentYear = async ({
  schoolId,
  studentIds,
  yearLevel
}: ManagedYearTagArgs) =>
  (
    await focusBearApi.put<ManagedYearTagResult[]>(
      `${MANAGED_API_PREFIX}/schools/${encodeURIComponent(schoolId)}/students/year-level`,
      { student_ids: studentIds, year_level: yearLevel }
    )
  ).data;
export const assignManagedStudents = ({
  schoolId,
  policyId,
  studentIds,
  replaceExisting
}: ManagedAssignmentArgs) =>
  focusBearApi.put(
    `${MANAGED_API_PREFIX}/schools/${encodeURIComponent(schoolId)}/assignments`,
    {
      policy_id: policyId,
      student_ids: studentIds,
      replace_existing: replaceExisting
    }
  );
export const removeManagedAssignment = (schoolId: string, studentId: string) =>
  remove(
    `/schools/${encodeURIComponent(schoolId)}/assignments/${encodeURIComponent(studentId)}`
  );
export const getHomePolicy = (studentId: string) =>
  get<ManagedPolicy>(`/children/${encodeURIComponent(studentId)}/home-policy`);
export const getManagedHistory = (policyId: string) =>
  get<ManagedPolicyHistory[]>(
    `/policies/${encodeURIComponent(policyId)}/history`
  );
export const getManagedParents = (studentId: string) =>
  get<ManagedParentGrant[]>(
    `/children/${encodeURIComponent(studentId)}/parents`
  );
export const inviteManagingParent = ({
  schoolId,
  studentId,
  email
}: ManagedInviteArgs) =>
  post<ManagedParentGrant>(
    `/schools/${encodeURIComponent(schoolId)}/children/${encodeURIComponent(studentId)}/parents`,
    { email }
  );
export const getManagingInvitations = () =>
  get<ManagedParentGrant[]>('/parent-grants/invitations');
export const acceptManagingInvitation = (id: string) =>
  post(`/parent-grants/${encodeURIComponent(id)}/accept`);
export const revokeManagingParent = (id: string) =>
  remove(`/parent-grants/${encodeURIComponent(id)}`);
export const getManagedSettingsRequests = () =>
  get<ManagedSettingsRequest[]>('/settings-edit-requests');
export const approveManagedSettingsRequest = (id: string) =>
  post(`/settings-edit-requests/${encodeURIComponent(id)}/approve`);
export const rejectManagedSettingsRequest = (id: string) =>
  post(`/settings-edit-requests/${encodeURIComponent(id)}/reject`);
export const commitManagedSettingsRequest = ({
  id,
  expectedRevision,
  changesHash
}: ManagedCommitArgs) =>
  post(`/settings-edit-requests/${encodeURIComponent(id)}/commit`, {
    expected_revision: expectedRevision,
    changes_hash: changesHash
  });
export const getManagedViolations = async (filter: ManagedViolationFilter) =>
  (
    await focusBearApi.get<ManagedViolation[]>(
      `${MANAGED_API_PREFIX}/violations`,
      { params: { ...filter, take: MANAGED_HISTORY_LIMIT } }
    )
  ).data;
export const getManagedAppeals = (id: string) =>
  get<ManagedAppeal[]>(`/violations/${encodeURIComponent(id)}/appeals`);

export const getMyEffectivePolicies = () =>
  get<ManagedEffectivePolicies>('/effective-policy');

export const getChildEffectivePolicies = (studentId: string) =>
  get<ManagedEffectivePolicies>(
    `/children/${encodeURIComponent(studentId)}/effective-policy`
  );

export const testManagedPolicyUrl = ({
  policyId,
  url,
  at
}: ManagedUrlTestArgs) =>
  post<ManagedUrlPreview>(
    `/policies/${encodeURIComponent(policyId)}/test-url`,
    { url, at }
  );

export const testManagedPolicyApp = ({
  policyId,
  platform,
  identifier
}: ManagedAppTestArgs) =>
  post<ManagedAppPreview>(
    `/policies/${encodeURIComponent(policyId)}/test-app`,
    { platform, identifier }
  );
